import * as yup from "yup";
import React, { useState } from "react";
import queryString from "query-string";
import { useQuery, useMutation } from "react-query";
import { useParams } from "react-router";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers";
import { Header, Loader, Form, Container, Button } from "semantic-ui-react";
import { subscribeAllEmails, getEmailSubscriptionGroups } from "../../../queries/communications";
import { ControlledInput } from "../../../components/ControlledForm";
import { useClient } from "../../../hooks/use-client";
import { useHistory } from "react-router-dom";
import { postNotifyInvite } from "../../../queries/notify";
import { formatPhoneNumber } from "../../../utils/FormatPhoneNumber";
import { useNotifySettingsPublic } from "../hooks/useNotifySettingsPublic";
import useNetworkHeaders from "../../../hooks/useNetworkHeaders";

type FormValues = {
  emailOrPhoneNumber: string;
};

export const Subscribe = () => {
  const { isConfigured, isLoading } = useNotifySettingsPublic();

  if (isLoading) {
    return (
      <Container className='tw-flex tw-h-full tw-items-center tw-justify-center'>
        <Loader active>Loading...</Loader>
      </Container>
    );
  }

  if (isConfigured) return <SubscribeWithSMS />;
  return <SubscribeNoSMS />;
};

export const SubscribeNoSMS = () => {
  const { site } = useParams<{ site: string }>();
  const { email } = queryString.parse(window.location.search);
  const networkHeaders = useNetworkHeaders();
  const history = useHistory();

  const cleanedEmailParam = typeof email === "string" ? email : null;

  const [submittedEmailOrPhoneNumber, setSubmittedEmailOrPhoneNumber] = useState<string | null>(null);
  const [didError, setDidError] = useState<boolean>(false);

  const { control, handleSubmit, errors } = useForm<FormValues>({
    resolver: yupResolver(
      yup.object().shape({
        emailOrPhoneNumber: yup
          .string()
          .required("Email Address is required")
          .test("emailOrPhoneNumber", "Invalid Email Address", (value: string) => {
            return isValidEmail(value);
          }),
      }),
    ),
    defaultValues: {
      emailOrPhoneNumber: cleanedEmailParam ?? "",
    },
  });

  const client = useClient(site);
  const clientData: { seal: string; name: string } | undefined = client.query.data?.payload?.data?.attributes;

  const subscriptionsQuery = useQuery(
    ["communications.subscriptions", email],
    getEmailSubscriptionGroups(networkHeaders),
    {
      enabled: !!email,
    },
  );
  const [subscribeEmailToAll, { isLoading: subscribeEmailToAllIsLoading }] = useMutation(
    subscribeAllEmails(networkHeaders),
    {
      onError: () => {
        setDidError(true);
      },
    },
  );

  const isMutationLoading = subscribeEmailToAllIsLoading;
  const isLoading = subscriptionsQuery.isLoading || client.query.isLoading;
  if (isLoading) {
    return (
      <Container className='tw-flex tw-h-full tw-items-center tw-justify-center'>
        <Loader active>Loading...</Loader>
      </Container>
    );
  }

  const goToUnsubscribe = (email: string) => {
    history.push(`/${site}/public/communications/unsubscribe?email=${encodeURIComponent(email)}`);
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    const valueIsEmail = isValidEmail(data.emailOrPhoneNumber);
    if (!valueIsEmail) return;

    await subscribeEmailToAll(data.emailOrPhoneNumber);
    await subscriptionsQuery.refetch();

    setSubmittedEmailOrPhoneNumber(data.emailOrPhoneNumber);
  };

  return (
    <div className='tw-p-3'>
      {!submittedEmailOrPhoneNumber && (
        <div className='tw-flex tw-w-full tw-flex-col tw-gap-3'>
          <Header className='tw-m-0'>
            <Header.Content className='tw-mb-1 tw-text-2xl'>{`Subscribe to ${clientData?.name} Notifications`}</Header.Content>
          </Header>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Form.Field required error={!!errors.emailOrPhoneNumber?.message} className='tw-mb-2'>
              <ControlledInput
                name='emailOrPhoneNumber'
                control={control}
                placeholder='Email Address'
                autoComplete='email'
                errors={errors}
                icon={"mail"}
                iconPosition={"left"}
                disabled={isMutationLoading}
              />
            </Form.Field>
            <Form.Field className='tw-mb-1'>
              <Button className='tw-m-0' primary type='submit' disabled={isMutationLoading} loading={isMutationLoading}>
                Subscribe
              </Button>
            </Form.Field>
            <div className='subText tw-text-sm'>
              By clicking subscribe, you agree to <a href='https://gogovapps.com/terms#sms'>Terms of Service</a>.
            </div>
          </Form>
        </div>
      )}
      {submittedEmailOrPhoneNumber && !didError && (
        <SuccessMessage
          thankYouSubheader={`You will now receive communication from ${clientData?.name} via email.`}
          shouldShowManageSubscription
          goToUnsubscribe={goToUnsubscribe}
          submittedEmailOrPhoneNumber={submittedEmailOrPhoneNumber}
        />
      )}
      {didError && (
        <ErrorMessage
          onRetry={async () => {
            setDidError(false);
            setSubmittedEmailOrPhoneNumber(null);
            await subscriptionsQuery.refetch();
          }}
        />
      )}
    </div>
  );
};

export const SubscribeWithSMS = () => {
  const { site } = useParams<{ site: string }>();
  const { email } = queryString.parse(window.location.search);
  const networkHeaders = useNetworkHeaders();
  const history = useHistory();

  const cleanedEmailParam = typeof email === "string" ? email : null;

  const [submittedEmailOrPhoneNumber, setSubmittedEmailOrPhoneNumber] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<"PHONE_NUMBER_OPTED_OUT" | "UNKNOWN" | null>(null);

  const { control, handleSubmit, errors } = useForm<FormValues>({
    resolver: yupResolver(
      yup.object().shape({
        emailOrPhoneNumber: yup
          .string()
          .required("Email Address or Phone Number is required")
          .test("emailOrPhoneNumber", "Invalid Email Address or Phone Number", (value: string) => {
            return isValidPhoneNumber(value) || isValidEmail(value);
          }),
      }),
    ),
    defaultValues: {
      emailOrPhoneNumber: cleanedEmailParam ?? "",
    },
  });

  const client = useClient(site);
  const clientData: { seal: string; name: string } | undefined = client.query.data?.payload?.data?.attributes;

  const subscriptionsQuery = useQuery(
    ["communications.subscriptions", email],
    getEmailSubscriptionGroups(networkHeaders),
    {
      enabled: !!email,
    },
  );
  const [subscribeEmailToAll, { isLoading: subscribeEmailToAllIsLoading }] = useMutation(
    subscribeAllEmails(networkHeaders),
    {
      onError: () => {
        setErrorType("UNKNOWN");
      },
    },
  );
  const [sendSMSInvite, { isLoading: sendSMSInviteIsLoading }] = useMutation(postNotifyInvite(networkHeaders), {
    onError: (e: { error?: { code?: "PHONE_NUMBER_OPTED_OUT" } }) => {
      if (e?.error?.code === "PHONE_NUMBER_OPTED_OUT") return setErrorType("PHONE_NUMBER_OPTED_OUT");
      setErrorType("UNKNOWN");
    },
  });

  const isMutationLoading = subscribeEmailToAllIsLoading || sendSMSInviteIsLoading;
  const isLoading = subscriptionsQuery.isLoading || client.query.isLoading;
  if (isLoading) {
    return (
      <Container className='tw-flex tw-h-full tw-items-center tw-justify-center'>
        <Loader active>Loading...</Loader>
      </Container>
    );
  }

  const goToUnsubscribe = (email: string) => {
    history.push(`/${site}/public/communications/unsubscribe?email=${encodeURIComponent(email)}`);
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    const valueIsEmail = isValidEmail(data.emailOrPhoneNumber);
    const valueIsPhoneNumber = isValidPhoneNumber(data.emailOrPhoneNumber);
    if (!valueIsEmail && !valueIsPhoneNumber) return;

    if (valueIsEmail) {
      await subscribeEmailToAll(data.emailOrPhoneNumber);
      await subscriptionsQuery.refetch();
    }

    // TODO: Add phone number subscription through core-service
    if (valueIsPhoneNumber) await sendSMSInvite({ phoneNumber: data.emailOrPhoneNumber });

    setSubmittedEmailOrPhoneNumber(data.emailOrPhoneNumber);
  };

  const submittedContactType = submittedEmailOrPhoneNumber
    ? isValidEmail(submittedEmailOrPhoneNumber)
      ? "email"
      : "phone"
    : null;
  const shouldShowManageSubscription = submittedEmailOrPhoneNumber && isValidEmail(submittedEmailOrPhoneNumber);
  const thankYouSubheader = `You will now receive communication from ${clientData?.name} via ${
    submittedContactType === "email" ? "email" : "sms"
  }.`;

  return (
    <div className='tw-p-3'>
      {!submittedEmailOrPhoneNumber && (
        <div className='tw-flex tw-w-full tw-flex-col tw-gap-3'>
          <Header className='tw-m-0'>
            <Header.Content className='tw-mb-1 tw-text-2xl'>{`Subscribe to ${clientData?.name} Notifications`}</Header.Content>
          </Header>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Form.Field required error={!!errors.emailOrPhoneNumber?.message} className='tw-mb-2'>
              <ControlledInput
                name='emailOrPhoneNumber'
                control={control}
                placeholder='Email Address or Phone Number'
                autoComplete='email'
                errors={errors}
                icon={submittedContactType === "phone" ? "phone" : "mail"}
                iconPosition={"left"}
                disabled={isMutationLoading}
              />
            </Form.Field>
            <Form.Field className='tw-mb-1'>
              <Button className='tw-m-0' primary type='submit' disabled={isMutationLoading} loading={isMutationLoading}>
                Subscribe
              </Button>
            </Form.Field>
            <div className='subText tw-text-sm'>
              By clicking subscribe, you agree to <a href='https://gogovapps.com/terms#sms'>Terms of Service</a>.
            </div>
          </Form>
        </div>
      )}
      {submittedEmailOrPhoneNumber && !errorType && (
        <SuccessMessage
          thankYouSubheader={thankYouSubheader}
          shouldShowManageSubscription={!!shouldShowManageSubscription}
          goToUnsubscribe={goToUnsubscribe}
          submittedEmailOrPhoneNumber={submittedEmailOrPhoneNumber}
        />
      )}
      {errorType === "PHONE_NUMBER_OPTED_OUT" && <PhoneNumberIsOptedOutMessage />}
      {errorType === "UNKNOWN" && (
        <ErrorMessage
          onRetry={async () => {
            setErrorType(null);
            setSubmittedEmailOrPhoneNumber(null);
            await subscriptionsQuery.refetch();
          }}
        />
      )}
    </div>
  );
};

const phoneRegExp = /^(\+\d{1,2}\s?)?1?\-?\.?\s?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;

function isValidPhoneNumber(value: string) {
  return yup.string().matches(phoneRegExp).isValidSync(value);
}

function isValidEmail(value: string) {
  return yup.string().email().isValidSync(value);
}

const SuccessMessage = ({
  thankYouSubheader,
  shouldShowManageSubscription,
  goToUnsubscribe,
  submittedEmailOrPhoneNumber,
}: {
  thankYouSubheader: string;
  shouldShowManageSubscription: boolean;
  goToUnsubscribe: (email: string) => void;
  submittedEmailOrPhoneNumber: string;
}) => (
  <div className='tw-flex tw-w-full tw-animate-fade-in tw-flex-col tw-gap-6 tw-transition-opacity'>
    <Header className='tw-m-0'>
      <Header.Content className='tw-mb-1 tw-text-2xl'>Thank you for subscribing!</Header.Content>
      <Header.Subheader>{thankYouSubheader}</Header.Subheader>
    </Header>
    {shouldShowManageSubscription && (
      <Button
        className='tw-m-0'
        primary
        onClick={() => {
          goToUnsubscribe(submittedEmailOrPhoneNumber);
        }}
      >
        Manage Subscription
      </Button>
    )}
  </div>
);

const PhoneNumberIsOptedOutMessage = () => {
  const { publicNotifySettings } = useNotifySettingsPublic();

  if (!publicNotifySettings?.sitePhoneNumber) return <Loader active inline size='small' />;

  return (
    <div className='tw-flex tw-w-full tw-animate-fade-in tw-flex-col tw-gap-6 tw-transition-opacity'>
      <Header className='tw-m-0'>
        <Header.Content className='tw-mb-1 tw-text-2xl'>Phone number is opted out</Header.Content>
        <Header.Subheader>
          This phone number has been opted out of receiving notifications. Please call or text JOIN to{" "}
          {formatPhoneNumber(publicNotifySettings?.sitePhoneNumber)} to opt back in.
        </Header.Subheader>
      </Header>
    </div>
  );
};

const ErrorMessage = ({ onRetry = () => {} }: { onRetry: () => void }) => (
  <div className='tw-flex tw-w-full tw-animate-fade-in tw-flex-col tw-gap-6 tw-transition-opacity'>
    <Header className='tw-m-0'>
      <Header.Content className='tw-mb-1 tw-text-2xl'>Failed to subscribe</Header.Content>
      <Header.Subheader>Something went wrong, try again?</Header.Subheader>
    </Header>
    <Button className='tw-m-0' primary onClick={onRetry}>
      Try Again
    </Button>
  </div>
);
